import axios, { AxiosRequestConfig } from "axios";
import { getCookieValue } from "../redux/utils/getCookie";
import { getTokenPair, setTokenPair } from "../features/auth/services/auth.service";
import * as userStore from "../features/user/store/user.store";

export function clearAuthorizationHeader() {
  delete axios.defaults.headers.common.Authorization;
}

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});

function isProtectedEndPoint(config: AxiosRequestConfig): boolean {
  return (
    Boolean(config.url?.length) &&
    !config.url?.includes("/token/") &&
    !config.url?.includes("users/reset-password-request/")
  );
}

axiosInstance.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    const csrftoken = getCookieValue("csrftoken");

    config.headers.Accept = config.headers.Accept ?? "application/json";
    config.headers["Content-Type"] = `${config.headers.Accept}; charset=utf-8`;
    config.headers["X-CSRFToken"] = csrftoken;

    const { accessToken } = getTokenPair();

    if (!accessToken && isProtectedEndPoint(config)) {
      userStore.logout();
    }

    if (accessToken) {
      config.headers.Authorization = "Bearer " + accessToken;
    }

    return config;
  },
  (err) => Promise.reject(err)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) =>
    new Promise((resolve, reject) => {
      const originalRequest = error.config;

      if (originalRequest.url === "/token/refresh/") {
        userStore.logout();
      } else if (error.response.data.code === "token_not_valid" && !error.config.__isRetryRequest) {
        originalRequest._retry = true;

        const { refreshToken } = getTokenPair();

        const response = axiosInstance
          .post("/token/refresh/", { refresh: refreshToken })
          .then((res) => {
            const newAccessToken = res.data.access;
            setTokenPair({ access: newAccessToken });
            resolve(response);

            originalRequest.headers.Authorization = "Bearer " + newAccessToken;
            return axios(originalRequest);
          })
          .catch((err) => {
            console.log(err);
            reject(err);
          });
      } else {
        reject(error);
      }
    })
);

export default axiosInstance;
